/**
 * Theme Redux Slice
 *
 * Stores the active client theme name and user color-mode preference.
 *
 * Dispatching:
 *   dispatch(setClientTheme('orange'))  — switch brand color theme
 *   dispatch(setColorMode('dark'))      — force dark mode for all screens
 *   dispatch(setColorMode('system'))    — follow OS / device setting (default)
 *   dispatch(setSystemColorScheme('dark')) — update current OS appearance
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ClientThemeId, ColorMode } from '../theme/clientThemes';

interface ThemeState {
  /** Currently active client brand theme */
  themeName: ClientThemeId;
  /**
   * User-controlled color mode preference.
   *   'system' — follow OS dark-mode setting (default)
   *   'light'  — always render in light mode
   *   'dark'   — always render in dark mode
   */
  colorMode: ColorMode;
  /**
   * Current OS/device appearance, updated via Appearance.addChangeListener
   * (native) or window.matchMedia (web). Defaults to 'light'.
   * Only used when colorMode === 'system'.
   */
  systemColorScheme: 'light' | 'dark';
}

const initialState: ThemeState = {
  themeName: 'blue',
  colorMode: 'system',
  systemColorScheme: 'light',
};

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    /**
     * Switch to a named client brand theme.
     * All screens that use `useScreenTheme` will re-render with the new brand colors.
     */
    setClientTheme(state, action: PayloadAction<ClientThemeId>) {
      state.themeName = action.payload;
    },

    /** Reset back to the default blue theme and system color mode */
    resetTheme(state) {
      state.themeName = 'blue';
      state.colorMode = 'system';
    },

    /**
     * Set the user's color mode preference.
     *   'system' → follow OS dark/light mode (default)
     *   'light'  → always light
     *   'dark'   → always dark
     */
    setColorMode(state, action: PayloadAction<ColorMode>) {
      state.colorMode = action.payload;
    },

    /**
     * Update the current OS/device appearance (light | dark).
     * Dispatched by Appearance.addChangeListener on native and
     * window.matchMedia on web so ALL Redux consumers re-render immediately.
     */
    setSystemColorScheme(state, action: PayloadAction<'light' | 'dark'>) {
      state.systemColorScheme = action.payload;
    },
  },
});

export const { setClientTheme, resetTheme, setColorMode, setSystemColorScheme } = themeSlice.actions;
export default themeSlice.reducer;